<template src="./template.html" />

<script>

import LandingPages from 'src/modules/pages/landing-page'
import { canInCollection } from 'naf-acl'
import Scaffolding from './scaffolding.vue'
import { mapState } from 'vuex'
import { firebase } from '@firebase/app'
const { Timestamp } = firebase.firestore
import moment from 'moment'
const DEFAULT_FILTER = [['metaData.byEmail', 'array-contains', null], ['@deleted', '==', false], ['isActive', '==', true]]
const DEFAULT_ORDER = ['dueDate', 'asc']
import StringUtils from 'utils/StringUtils'
import FilterBox from '@/components/filter-box'

export default {
  name: 'ManageTask',
  mixins: [LandingPages, Scaffolding],
  components: {
    FilterBox
  },
  props: {
    title: {
      type: String,
      default: () => 'Quản lý công việc'
    },
    queryConditions: {
      type: Object,
      default: () => ({
        ref: ['Tasks'],
        filter: DEFAULT_FILTER,
        order: DEFAULT_ORDER,
        limit: 50,
        returnSnapshot: true 
      })
    },
    queryByUser: {
      type: Boolean,
      default: true
    },
    backLink: {
      type: String,
      default: () => '/'
    },
    routeDetailName: {
      type: String,
      default: ''
    },
    headers: {
      type: Array,
      default: () => [
        {
          label: 'No',
          align: 'left',
          sortable: false,
          name: 'no',
          field: 'no'
        },
        {
          label: 'Phòng ban',
          align: 'left',
          sortable: true,
          sort: (a, b) => a.name.localeCompare(b.name),
          name: 'department.id',
          field: 'department',
          labelFormat: `$this ? $this.name : ''`
        },
        {
          label: 'Tiêu đề',
          align: 'left',
          name: 'title',
          field: 'title',
          sortable: true,
          sort: (a, b) => a.localeCompare(b),
          type: 'Label',
          tdStyle: `white-space: normal; overflow: hidden; text-overflow: ellipsis; max-width: 350px; min-width:350px; word-break: break-word`,
          config: {
            watch: true,
            icon: {
              name: `this.rootModel.type.id === 'idea' ? 'fas fa-lightbulb' : 'fas fa-calendar-check'`,
              color: 'green'
            }
          }
        },
        {
          label: 'Người phụ trách',
          align: 'left',
          name: 'assignee',
          field: 'assignee',
        },
        {
          label: 'Người nhận kết quả',
          align: 'center',
          name: 'reporter',
          field: 'reporter',
        },
        {
          label: 'Người cập nhật',
          align: 'center',
          name: 'assistant',
          field: 'assistant',
        },
        {
          label: 'Người tạo',
          align: 'center',
          name: 'metaData',
          field: 'metaData',
          labelFormat: '$this && $this.createdBy'
        },
        {
          label: 'Hạn chót',
          align: 'center',
          name: 'dueDate',
          field: 'dueDate',
          sortable: true,
          sort: (a, b) => a.toDate().getTime() > b.toDate().getTime() ? 1 : -1,
          labelFormat: `$this && typeof $this.toDate === 'function' ? moment.utc($this.toDate()).format('DD/MM/YYYY') : ''`
        },
        {
          label: 'Quá hạn',
          align: 'center',
          name: 'dueDate2',
          field: 'dueDate',
          type: 'Label',
          config: {
            format: `(this.modelProps && this.modelProps instanceof Timestamp && this.moment(this.modelProps.toDate()).isBefore() && this.rootModel.status.id !== 'done' && this.rootModel.status.id !== 'cancel' ? this.moment(this.modelProps.toDate()).fromNow() : '')`,
            chipMode: true,
            colorChip: `'red'`,
            show: `this.modelProps && this.modelProps instanceof Timestamp && this.moment(this.modelProps.toDate()).isBefore() && this.rootModel.status.id !== 'done' && this.rootModel.status.id !== 'cancel'`
          }
        },
        {
          label: 'Độ ưu tiên',
          align: 'center',
          name: 'priority.value',
          field: 'priority',
          type: 'Label',
          sortable: true,
          sort: (a, b) => a.value > b.value ? 1 : -1,
          config: {
            format: `this.value ? this.value.label: ''`,
            icon: {
              name: `this.value && this.value.icon ? this.value.icon.name : ''`,
              color: `this.value && this.value.icon ? this.value.icon.color: ''`
            },
            watch: true
          }
        },
        {
          label: 'Tiến độ',
          align: 'center',
          name: 'progress',
          field: 'progress',
          type: 'Label',
          sortable: true,
          sort: (a, b) => a.name.localeCompare(b.name),
          config: {
            format: `this.value ? this.value + ' %' : '0 %'`,
            showWhenNull: true,
            chipMode: true,
            colorChip: `'green'`,
            watch: true
          }
        },
        {
          label: 'Trạng thái',
          align: 'center',
          name: 'status',
          field: 'status',
          type: 'Label',
          sortable: true,
          sort: (a, b) => a.name.localeCompare(b.name),
          config: {
            format: 'this.value.label',
            chipMode: true,
            colorChip: `this.value.color`,
            watch: true
          }
        }
      ]
    },
    idFieldName: {
      type: String,
      default: ''
    },
    canCreate: {
      type: Boolean,
      default: () => true
    },
    canCopy: {
      type: Boolean,
      default: () => false
    },
    canDelete: {
      type: Boolean,
      default: () => undefined
    },
    tableConfig: {
      type: Object,
      default: () => ({
        pagination: {
          'rowsPerPage': 100,
          'rowsNumber': 100,
          'descending': false,
          sortBy: 'dueDate'
        }
      })
    },
    useOneMeta: {
      type: Boolean,
      default: () => false
    },
    detailRoute: {
      type: String,
      default: '/task-management/task-detail'
    },
    useFilter: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    queryConditions(newValue, oldValue) {
      this.privateQueryCondition = this._.cloneDeep(newValue)
      if(this.queryByUser) {
        this.DEFAULT_FILTER[0][2] = this.$store.state.user.email
        this.privateQueryCondition.filter[0][2] = this.$store.state.user.email
        this.defaultQuery.filter[0][2] = this.$store.state.user.email
      } else {
        this.DEFAULT_FILTER = [['@deleted', '==', false], ['isActive', '==', true]]
        this.privateQueryCondition.filter = [['@deleted', '==', false], ['isActive', '==', true]]
        this.defaultQuery.filter = [['@deleted', '==', false], ['isActive', '==', true]]
      }
      if(this._.isEqual(newValue, oldValue)) return
      this.fetchData()
    }
  },
  async created() {
    this.fetchUsers()
    this.firstRun = true
    this.privatePagination = this.R.clone(this.tableConfig.pagination)
    this.DEFAULT_FILTER = DEFAULT_FILTER
    if(this.queryByUser) {
      this.DEFAULT_FILTER[0][2] = this.$store.state.user.email
      this.defaultQuery.filter[0][2] = this.$store.state.user.email
      this.privateQueryCondition.filter[0][2] = this.$store.state.user.email
    } else {
      this.DEFAULT_FILTER = [['@deleted', '==', false], ['isActive', '==', true]]
      this.defaultQuery.filter = [['@deleted', '==', false], ['isActive', '==', true]]
      this.privateQueryCondition.filter = [['@deleted', '==', false], ['isActive', '==', true]]
    }
    await this.commitTaskMasterData()
    this.stopFetch = true
    // this.recoverFilter()
  },
  data() {
    return {
      chooseTypeDialog: false,
      scaffolding: Scaffolding,
      pickField: this.headers.map(item => item.field).concat(['_id', '@deleted', '__index', 'position', 'id', 'type']),
      R: this.R,
      defaultQuery: this.R.clone(this.queryConditions),
      DEFAULT_FILTER: DEFAULT_FILTER,
      filterDialog: false,
      conditions: [
        {
          show: true,
          config: {
            type: 'DatePicker',
            label: 'Hạn chót từ',
            fieldName: 'dueDateFrom'
          },
          mapLabel: (value) => {
            if(value.toDate) {
              return moment(value.toDate()).format('DD/MM/YYYY')
            }
            return ''
          },
          query: ['dueDate', '>=', 'this']
        },
        {
          show: true,
          config: {
            label: 'Hạn chót đến',
            fieldName: 'dueDateTo',
            type: 'DatePicker',
          },
          mapLabel: (value) => {
            if(value.toDate) {
              return moment(value.toDate()).format('DD/MM/YYYY')
            }
            return ''
          },
          query: ['dueDate', '<=', 'this']
        },
        {
          config: {
            datasource: {
              options: [],
              label: 'label',
              value: 'id',
              color: 'color',
              emitValue: true
            },
            label: 'Trạng thái',
            useChip: true,
            mapOptions:true,
            icon: 'linear_scale',
            sortBy: 'value',
            fieldName: 'status',
            clearable: true
          },
          mapLabel: (value, options) => {
            let filter = options.filter(item => item.id === value)
            if(filter[0]) {
              return filter[0].label
            }
            return ''
          },
          show: true,
          query: ['status.id', '==', 'this']
        },
        {
          config: {
            label: 'Người phụ trách',
            icon: 'people',
            datasource: {
              options: [],
              value: 'email',
              label: 'displayName',
              emitValue: true
            },
            after: {
              func: this.assignToMe
            },
            mapOptions: true,
            useInput: true,
            fieldName: 'assignee',
            clearable: true
          },
          show: true,
          query: ['assignee', '==', 'this']
        },
        {
          config: {
            label: 'Người nhận kết quả',
            icon: 'people',
            datasource: {
              options: [],
              value: 'email',
              label: 'displayName',
              emitValue: true
            },
            after: {
              func: this.assignToMe
            },
            mapOptions: true,
            useInput: true,
            fieldName: 'reporter',
            clearable: true
          },
          show: true,
          query: ['reporter', '==', 'this']
        },
        {
          config: {
            label: 'Người cập nhật',
            icon: 'people',
            datasource: {
              options: [],
              value: 'email',
              label: 'displayName',
              emitValue: true
            },
            after: {
              func: this.assignToMe
            },
            mapOptions: true,
            useInput: true,
            fieldName: 'assistant',
            clearable: true
          },
          show: true,
          query: ['assistant', '==', 'this']
        },
        {
          config: {
            label: 'Phòng ban',
            icon: 'store',
            datasource: {
              options: [],
              label: 'name',
              value: 'id',
              emitValue: true
            },
            mapOptions: true,
            sortBy: 'name',
            useInput: true,
            fieldName: 'department',
            clearable: true
          },
          mapLabel: (value, options) => {
            let filter = options.filter(item => item.id === value)
            if(filter[0]) {
              return filter[0].name
            }
            return ''
          },
          show: true,
          query: ['department.id', '==', 'this']
        }
      ],
      disableFreeText: false,
      conditionsFreeText: {
        config: {
          label: 'Nhãn',
          fieldName: 'labels',

        },
        query: ['metaData.labels', 'array-contains', 'this']
      },
      firstRun: true
    }
  },
  computed: mapState(['masterdata', 'user']),
  methods: {
    addButtonClick() {
      this.chooseTypeDialog = true
    },
    async routeDetail(id) {
      let dataFetched = await this.$query(['Tasks', id], null, null, null, null, null, 'namis')
      this.$router.push({
        name: this.detailRoute,
        query: {
          id: id
        },
        params: {
          id: id,
          mode: 'EDIT',
          data: dataFetched,
          scaffolding: this.parrentScaffolding[dataFetched.type.id]
        }
      })
    },
    handlerCreate(type) {
      this.$router.push({
        name: '/task-management/task-detail',
        params: {
          mode: 'CREATE',
          scaffolding: {
            scaff: this.parrentScaffolding[type],
            type: type
          }
        }
      })
    },
    async submitDelete () {
      let vm = this
      vm.showLoading()
      this.isLoading = true
      this.isDeleting = true
      let submitModel = { id: this.selectedRow._id, data: [] }
      submitModel.data['@deleted'] = true
      let result = vm.$command.execute('deleteTaskManagemennt', submitModel)
      try {
        await result.then(() => {
          vm.$q.notify({
            message: 'Đã xóa thành công',
            color: 'black',
            icon: 'check'
          })
          vm.fetchData()
        })
      } catch (error) {
        vm.$q.notify({
          message: error.toString(),
          color: 'red',
          icon: 'error'
        })
      }
      this.isLoading = false
      this.isDeleting = false
      vm.hideLoading()
    },
    checkDelete(item) {
      if(!this.R.isNil(this.canDelete)) return this.canDelete
      return this.user.email === this.R.path(['metaData', 'createdBy'], item) || this.user.email === this.R.path(['reporter'], item)
    },
    fetch,
    async commitTaskMasterData() {
      let vm = this
      if(!vm.masterdata.taskMasterData) {
        await vm.$query(['MasterData', 'TaskMasterData']).then(res => {
          vm.$store.dispatch('registerTaskMasterData', res)
        })
      }
      // department
      vm.$query(['MasterData', 'Department']).then(res => {
        vm.conditions[6].config.datasource.options = vm.R.values(vm.R.omit(['metaData'],res))
      })
    },
    async fetchData () {
      let vm = this
      if(vm.firstRun && vm._.isEqual(vm.privateQueryCondition.filter, vm.DEFAULT_FILTER)) return vm.taskCustomQuery()
      vm.isLoading = true
      if(vm.privateQueryCondition) {
        await vm.$query(vm.privateQueryCondition.ref, vm.privateQueryCondition.filter, vm.privateQueryCondition.order,
        vm.privateQueryCondition.limit, vm.privateQueryCondition.after, vm.privateQueryCondition.returnSnapshot, 'namis').then(res => {
          if(res.error) throw new Error(res.error.message)
          if(!res.data) {
            vm.data = []
            return
          }
          let temp = Array.isArray(res.data) ? res.data : res[vm.privateQueryCondition.fieldName]
          vm.data = temp.map((item, index) => {
            item.position = index
            return vm.R.pick(vm.pickField, item)
          })
          vm.res = res
          vm.cacheData(vm.data)
          vm.mapData()
        }).catch(e => {
          vm.$q.notify({
            message: `${e}`,
            color: 'red',
            icon: 'error'
          })
          console.log(e)
        }).finally(() => {
          vm.isLoading = false
        })
      }
    },
    fetchUsers() {
      this.$query(['Users', 'UsersIndex']).then(res => {
        this.conditions[3].config.datasource.options = res.list
        this.conditions[4].config.datasource.options = res.list
        this.conditions[5].config.datasource.options = res.list
      })
    },
    async taskCustomQuery() {
      let vm = this
      vm.isLoading = true
      vm.showLoading()
      if(vm.privateQueryCondition) {
        let filterByDonePending = vm.R.concat(vm.DEFAULT_FILTER, [['status.id', '==', 'done_pending']])
        let filterByCancelPending = vm.R.concat(vm.DEFAULT_FILTER, [['status.id', '==', 'cancel_pending']])
        let isActive = vm.DEFAULT_FILTER
        let donePending = vm.$query(vm.privateQueryCondition.ref, filterByDonePending, vm.privateQueryCondition.order,
        vm.privateQueryCondition.limit, null, vm.privateQueryCondition.returnSnapshot, 'namis')
        let cancelPending = vm.$query(vm.privateQueryCondition.ref, filterByCancelPending, vm.privateQueryCondition.order,
        vm.privateQueryCondition.limit, null, vm.privateQueryCondition.returnSnapshot, 'namis')
        let isActiveP = vm.$query(vm.privateQueryCondition.ref, isActive, vm.privateQueryCondition.order,
        vm.privateQueryCondition.limit, null, vm.privateQueryCondition.returnSnapshot, 'namis')
        await Promise.all([donePending, cancelPending, isActiveP]).then(res => {
          let dataDonePending = vm.R.path(['0', 'data'], res)
          let dataCancelPending = vm.R.path(['1', 'data'], res)
          let dataIsActive = vm.R.path(['2', 'data'], res)
          let result = vm._.unionBy(dataDonePending, dataCancelPending, dataIsActive, item => item._id)
          vm.data = result.map((item, index) => {
            item.position = index
            return vm.R.pick(vm.pickField, item)
          })
          vm.data = vm.data.slice(0, vm.privateQueryCondition.limit)
          vm.cacheData(vm.data)
          vm.mapData()
        }).catch(error => {
            vm.$q.notify({
              message: `${error}`,
              color: 'red',
              icon: 'error'
            })
        }).finally(() => {
          vm.firstRun = false
          vm.isLoading = false
          vm.hideLoading()
        })
      }
      vm.firstRun = false
      vm.hideLoading()
      vm.isLoading = false
    },
    mapData() {
      let vm = this
      if (vm.masterdata && vm.masterdata.taskMasterData) {
      let listPriorityById = vm._.keyBy(vm.R.flatten([vm.R.path(['taskMasterData', 'listPriority'], vm.masterdata)]), 'id')
        vm.data.forEach(element => {
          element.status = vm.masterdata.taskMasterData.listStatus[vm.R.path(['status', 'id'], element)]
          element.priority = listPriorityById[vm.R.path(['priority', 'id'], element)]
        });
        // status
        vm.conditions[2].config.datasource.options = vm.R.concat(vm.R.values(vm.R.path(['taskMasterData', 'listStatus'], vm.masterdata)), [{
          label: 'Quá hạn',
          id: 'overdue',
          color: 'red',
          value: 6
        }])
      }
    },
    query(value) {
      this.privateQueryCondition = value
      let queryHaveStatus = this.privateQueryCondition.filter.filter(item => item[0] === 'status.id')
      let isDueDate = false
      if(queryHaveStatus.length > 0) {
        this.privateQueryCondition.filter = this.privateQueryCondition.filter.filter(item => item[0] !== 'isActive')
        .map(item => {
          if(item[2] === 'overdue') {
            item = ['dueDate', '<=', Timestamp.fromDate(moment.utc().toDate())]
            isDueDate = true
          }
          return item
        })
        this.privateQueryCondition.filter = isDueDate ? this.R.append(['isActive', '==', true], this.privateQueryCondition.filter) : this.privateQueryCondition.filter
      }
      this.fetchData()
    },
    assignToMe(components, value, options) {
      if(this.$store.getters.user.email) {
        components.$emit('input', this.$store.getters.user.email)
      }
    }
  }
}
</script>
